import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { AuthService } from '../../../core/services/auth.service';
import { ProcessService } from '../../../core/services/process.service';
import { Process } from '../../../core/models/process.model';
import { LoadingSpinnerComponent } from '../../components/loading-spinner/loading-spinner.component';
import { TruncatePipe } from '../../pipes/truncate.pipe';

type ProcessStatus = 'A iniciar' | 'Em andamento' | 'Concluído' | 'Fechado';

interface StatusBoxVm {
  status: ProcessStatus;
  title: string;
  processes: Process[];
  loading: boolean;
  error: boolean;
}

@Component({
  selector: 'app-ui-latest-processes-by-status',
  standalone: true,
  imports: [CommonModule, RouterModule, LoadingSpinnerComponent, TruncatePipe],
  templateUrl: './ui-latest-processes-by-status.component.html',
})
export class UiLatestProcessesByStatusComponent implements OnInit, OnDestroy {
  public boxes: StatusBoxVm[] = [
    {
      status: 'A iniciar',
      title: 'Últimos processos (A iniciar)',
      processes: [],
      loading: false,
      error: false,
    },
    {
      status: 'Em andamento',
      title: 'Últimos processos (Em andamento)',
      processes: [],
      loading: false,
      error: false,
    },
    {
      status: 'Concluído',
      title: 'Últimos processos (Concluído)',
      processes: [],
      loading: false,
      error: false,
    },
    {
      status: 'Fechado',
      title: 'Últimos processos (Fechado)',
      processes: [],
      loading: false,
      error: false,
    },
  ];

  private destroy$ = new Subject<void>();

  constructor(
    private processService: ProcessService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.loadAll();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public trackByProcessId(_: number, p: Process): string {
    return p.id;
  }

  public getStatusBadgeClass(status: ProcessStatus): string {
    switch (status) {
      case 'Em andamento':
        return 'badge bg-warning/10 !text-warning';
      case 'Concluído':
        return 'badge bg-success/10 !text-success';
      case 'A iniciar':
        return 'badge bg-info/10 !text-info';
      case 'Fechado':
        return 'badge bg-danger/10 !text-danger';
      default:
        return 'badge bg-secondary/10 !text-secondary';
    }
  }

  public retry(status: ProcessStatus): void {
    const box = this.boxes.find((b) => b.status === status);
    if (!box) return;
    this.loadBox(box);
  }

  private loadAll(): void {
    if (!this.authService.isAuthenticated()) {
      this.boxes = this.boxes.map((b) => ({ ...b, error: true, loading: false }));
      return;
    }

    this.boxes.forEach((box) => this.loadBox(box));
  }

  private loadBox(box: StatusBoxVm): void {
    box.loading = true;
    box.error = false;

    this.processService
      .getProcesses({
        page: 1,
        per_page: 5,
        status: box.status,
        sort_by: 'created_at',
        sort_order: 'desc',
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            box.processes = response.data;
          } else {
            box.error = true;
          }
          box.loading = false;
        },
        error: () => {
          box.error = true;
          box.loading = false;
        },
      });
  }
}
